import React from 'react';
import { Truck, Package, RotateCcw, Clock, MapPin, AlertCircle } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';

const ShippingAndExchange = () => {
    const shippingOptions = [
        {
            icon: Truck,
            title: 'Standard Shipping',
            time: '5-7 Business Days',
            cost: 'Free on orders above \u20B95,000'
        },
        {
            icon: Package,
            title: 'Express Shipping',
            time: '2-3 Business Days',
            cost: '\u20B9299'
        },
        {
            icon: MapPin,
            title: 'International Shipping',
            time: '10-15 Business Days',
            cost: 'Calculated at checkout'
        },
    ];

    const exchangeSteps = [
        { step: '1', title: 'Initiate Request', description: 'Log in to your account and go to My Orders. Select the item and choose "Return/Exchange" within 7 days of delivery.' },
        { step: '2', title: 'Package the Item', description: 'Pack the item in its original packaging with all tags attached. Include the return slip from your order.' },
        { step: '3', title: 'Schedule Pickup', description: 'Our courier partner will pick up the item from your address within 2-3 business days of your request.' },
        { step: '4', title: 'Refund / Exchange', description: 'Once we receive and inspect the item, your refund will be processed within 7-10 business days, or your exchange will be shipped.' },
    ];

    const policies = [
        { title: 'Eligible for Return', items: ['Unused items in original condition', 'Items with all tags attached', 'Returns within 7 days of delivery', 'Items in original packaging'] },
        { title: 'Not Eligible for Return', items: ['Custom-made or bridal orders', 'Altered or worn items', 'Items without tags', 'Sale/clearance items', 'Intimate wear and accessories'] },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-20 md:pt-24">
                {/* Hero */}
                <div className="bg-[#F0F7FA] py-10 md:py-16 px-4 text-center">
                    <div className="w-12 h-12 bg-[#1B4965] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Truck className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-semibold text-[#1B4965] mb-3">Shipping & Exchange</h1>
                    <p className="text-sm text-[#6B7280] max-w-lg mx-auto">
                        Everything you need to know about shipping, returns, and exchanges
                    </p>
                </div>

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    {/* Shipping Options */}
                    <section className="mb-12">
                        <h2 className="text-xl font-semibold text-[#1B4965] mb-6">Shipping Options</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {shippingOptions.map((option, i) => {
                                const Icon = option.icon;
                                return (
                                    <div key={i} className="bg-[#F0F7FA] p-6 rounded-sm text-center">
                                        <Icon className="w-8 h-8 text-[#1B4965] mx-auto mb-3" />
                                        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1">{option.title}</h3>
                                        <p className="text-xs text-[#62B6CB] font-medium mb-1">{option.time}</p>
                                        <p className="text-xs text-[#6B7280]">{option.cost}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Exchange Process */}
                    <section className="mb-12">
                        <h2 className="text-xl font-semibold text-[#1B4965] mb-6">Return & Exchange Process</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {exchangeSteps.map((item, i) => (
                                <div key={i} className="flex gap-4 p-4 border border-[#E5E7EB] rounded-sm">
                                    <div className="w-8 h-8 bg-[#1B4965] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                                        {item.step}
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1">{item.title}</h3>
                                        <p className="text-xs text-[#6B7280] leading-relaxed">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Return Eligibility */}
                    <section className="mb-12">
                        <h2 className="text-xl font-semibold text-[#1B4965] mb-6">Return Eligibility</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {policies.map((policy, i) => (
                                <div key={i} className={`p-6 rounded-sm ${i === 0 ? 'bg-[#F0F7FA] border border-[#1B4965]/10' : 'bg-white border border-[#E5E7EB]'}`}>
                                    <h3 className={`text-sm font-semibold mb-4 ${i === 0 ? 'text-[#1B4965]' : 'text-[#6B7280]'}`}>{policy.title}</h3>
                                    <ul className="space-y-2">
                                        {policy.items.map((item, j) => (
                                            <li key={j} className="flex items-start gap-2 text-sm text-[#6B7280]">
                                                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${i === 0 ? 'bg-[#62B6CB]' : 'bg-[#E5E7EB]'}`} />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Note */}
                    <div className="flex gap-4 p-4 bg-[#F0F7FA] rounded-sm border border-[#1B4965]/10">
                        <AlertCircle className="w-5 h-5 text-[#1B4965] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-[#1A1A1A] mb-1">Important Note</p>
                            <p className="text-xs text-[#6B7280] leading-relaxed">
                                For any questions regarding shipping or returns, please contact our customer support team at support@neela.in or call +91 98765 43210. We're available Monday to Saturday, 10 AM to 7 PM IST.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ShippingAndExchange;
